/* The one-click starters beside the drop zone.
 *
 * WHY THESE ARE RECIPES AND NOT PICTURES.
 *
 * The obvious thing to put here is the shelf of templates every meme site has:
 * the Yoda still, the Drake frames, the distracted boyfriend. Those sites do
 * not own any of it. Imgflip's terms take a licence and an indemnity FROM THE
 * UPLOADER and run DMCA notice-and-takedown, which puts the shelf inside the
 * §512(c) safe harbour for material "stored at the direction of a user" -- it
 * is a user-upload archive sorted by popularity, not a licensed collection.
 *
 * None of that transfers to a shelf WE choose. Material an operator selects and
 * ships is the operator's own act, outside that safe harbour entirely, and
 * hand-picking recognisable film frames is knowledge rather than the absence of
 * it. This site also has no registered agent and no accounts to terminate, so
 * it would not qualify even where the safe harbour applied. And terms.html §3.2
 * forbids visitors from using the Service as an image host, which is exactly
 * what hosting a shelf would make it.
 *
 * So nothing here is a file. Each entry is a few lines of canvas that the
 * visitor's own browser runs, producing a picture that never existed anywhere
 * until they clicked. Nothing is served, nothing is stored, and there is
 * nothing to clear the rights to.
 *
 * `kind` is the seam that keeps this from being a dead end. A future entry
 * could be {kind: "hosted", src} -- a photograph somebody has actually licensed
 * -- and only the click handler in MemeControl.vue would need to learn about
 * it. That is a legal decision with a takedown process attached, though, not a
 * follow-up commit.
 *
 * The colours live here and not in the component because tests/test_palette.py
 * forbids a raw hex in a .vue: the interface has to follow the theme, and these
 * are the opposite of that -- a white card stays white on a dark page, because
 * it is the visitor's picture rather than our furniture.
 */

/* Square, and large enough that a caption at the default size is still crisp
 * on a phone screen at full width, without making a flat colour into a
 * megabyte. A blank is nearly all one colour, so PNG costs almost nothing. */
export const STARTER_SIZE = 1200

const INK = "#101014"
const PAPER = "#ffffff"
const DUSK_TOP = "#2b1e5c"
const DUSK_BOTTOM = "#b3365e"
const SPOT_INNER = "#3a3a46"
const SPOT_OUTER = "#0b0b0f"

export const MEME_STARTERS = [
	{
		key: "white",
		kind: "generated",
		swatch: PAPER,
		draw(ctx, w, h) {
			ctx.fillStyle = PAPER
			ctx.fillRect(0, 0, w, h)
		},
	},
	{
		key: "black",
		kind: "generated",
		swatch: INK,
		draw(ctx, w, h) {
			ctx.fillStyle = INK
			ctx.fillRect(0, 0, w, h)
		},
	},
	{
		key: "dusk",
		kind: "generated",
		swatch: DUSK_BOTTOM,
		draw(ctx, w, h) {
			const g = ctx.createLinearGradient(0, 0, w, h)
			g.addColorStop(0, DUSK_TOP)
			g.addColorStop(1, DUSK_BOTTOM)
			ctx.fillStyle = g
			ctx.fillRect(0, 0, w, h)
		},
	},
	{
		key: "spotlight",
		kind: "generated",
		swatch: SPOT_INNER,
		draw(ctx, w, h) {
			// Radial, so a caption in the middle sits on the lighter part and
			// the edges stay out of the way of it.
			const g = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, Math.max(w, h) * 0.7)
			g.addColorStop(0, SPOT_INNER)
			g.addColorStop(1, SPOT_OUTER)
			ctx.fillStyle = g
			ctx.fillRect(0, 0, w, h)
		},
	},
]

export const STARTER_KEYS = MEME_STARTERS.map((s) => s.key)

export function starterFor(key) {
	return MEME_STARTERS.find((s) => s.key === key) || null
}

/* Run one of the recipes and hand back a File, so a starter enters the
 * application through exactly the same door as a dropped photograph.
 *
 * That is the point of returning a File rather than a canvas or a data URL:
 * addFiles() already knows how to accept, order, preview and transform one, and
 * a second intake path for pictures we made ourselves would be a second place
 * for every future rule about intake to be forgotten.
 *
 * PNG, because a flat colour or a smooth gradient is a handful of kilobytes
 * lossless and would be larger and worse as a JPEG. outputType() in
 * imageTransform.js keeps a PNG a PNG, so the caption is drawn onto it without
 * a generation of chroma loss.
 */
export async function starterToFile(starter, size = STARTER_SIZE) {
	if (!starter || typeof starter.draw !== "function") return null
	const canvas = document.createElement("canvas")
	canvas.width = size
	canvas.height = size
	try {
		const ctx = canvas.getContext("2d")
		if (!ctx) return null
		starter.draw(ctx, size, size)
		const blob = await new Promise((resolve) => {
			if (canvas.toBlob) canvas.toBlob(resolve, "image/png")
			else resolve(null)
		})
		if (!blob) return null
		return new File([blob], `${starter.key}.png`,
			{type: "image/png", lastModified: Date.now()})
	} finally {
		// Handed back straight away, the way transformImage() does it: a
		// visitor trying all four starters should not leave four of these
		// behind.
		canvas.width = 0
		canvas.height = 0
	}
}
